/* eslint-disable promise/prefer-await-to-then */
import { DefaultButton, Dropdown, IDropdownOption, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, ProgressIndicator, Stack, StackItem, TextField } from "@fluentui/react"
import { useEffect, useState } from "react"
import { usePrevious } from "../hooks/generalHooks"
import { getErrorText, IS_ADMIN, maskAsDate, safeFormatDateValue, safeParseDateTime } from "../lib/helper"
import { makeACMGCRMApiCall, makeBrokerAPICall, makeCRMApiCall } from "../services/apiService"
import { IAccount } from "../types/IAccount"
import { IEmployee, IEmployeeCreate } from "../types/IEmployee"
import { IEnrollmentRecord } from "../types/IEnrollmentRecord"
import { IGroupPlan } from "../types/IGroupPlan"
import { IHouseholdMember, IHouseholdMemberEnrollmentManager } from "../types/IHouseholdMember"

interface EditHouseholdMemberPanelProps {
	householdMember: IEnrollmentRecord
	closePanel: (refreshItems: boolean, success: boolean) => void
	addHouseholdMember: boolean
	isEmployee: boolean
	employeeId?: string
	groupPlans: IGroupPlan[]
	group: IAccount
}

export function EditHouseholdMemberPanel({ householdMember, closePanel, addHouseholdMember, employeeId, isEmployee, groupPlans, group }: EditHouseholdMemberPanelProps) {
	const [form, setForm] = useState<Partial<IHouseholdMember>>(householdMember?.record || {})
	const [formEmployee, setFormEmployee] = useState<Partial<IEmployee>>()
	const originalForm = usePrevious(form)
	const [error, setError] = useState<string | undefined>()
	const [saving, setSaving] = useState(false)
	const [originalEmployee, setOriginalEmployee] = useState<Partial<IEmployee>>()
	const [originalHHMember, setOriginalHHMember] = useState<Partial<IHouseholdMember>>(householdMember?.record || {})
	const isProspect = group.cw_accounttype.includes("Prospect")

	useEffect(() => {
		if (isEmployee && employeeId) {
			makeCRMApiCall(`cw_employees(${employeeId})`)
				.then(employee => {
					setOriginalEmployee(employee)
					setFormEmployee(employee)
				})
				.catch(err => {
					console.error(err)
				})
		} else if (isEmployee && !employeeId) {
			// THIS IS A NEW EMPLOYEE
			setFormEmployee({})
			setOriginalEmployee({})
			setForm({
				cw_relationship: 0
			})
			setOriginalHHMember({
				cw_relationship: 0
			})
		}
	}, [isEmployee, employeeId])

	const madeChanges = () => {
		let retVal = false
		if (originalEmployee && originalHHMember) {
			if (isEmployee) {
				const employeeFormBody = getEmployeeSaveBody()
				if (employeeFormBody) {
					retVal = Object.values(employeeFormBody).length > 0
				}
			}
		}
		if (!retVal) {
			const formBody = getFormSaveBody()
			if (formBody) {
				retVal = Object.values(formBody).length > 0
			}
		}
		return retVal
	}

	const isComplete = () => {
		const complete = form.cw_firstname && form.cw_lastname && form.cw_birthdate && form.cw_gender
		return isEmployee ? complete && formEmployee.cw_zipcode : complete && form.cw_hhzip && form.cw_relationship
	}

	const getFormSaveBody = (): Partial<IHouseholdMember> => {
		const body: Partial<IHouseholdMember> = {}
		if (originalHHMember.cw_firstname !== form.cw_firstname || originalHHMember.cw_lastname !== form.cw_lastname) {
			body.cw_name = `${form.cw_firstname} ${form.cw_lastname}`
			if (originalHHMember.cw_firstname !== form.cw_firstname) {
				body.cw_firstname = form.cw_firstname
			}
			if (originalHHMember.cw_lastname !== form.cw_lastname) {
				body.cw_lastname = form.cw_lastname
			}
		}
		if (originalHHMember.cw_insurancestartdate !== form.cw_insurancestartdate) {
			body.cw_insurancestartdate = form.cw_insurancestartdate
		}
		if (originalHHMember.cw_birthdate !== form.cw_birthdate) {
			body.cw_birthdate = form.cw_birthdate
		}
		if (originalHHMember.cw_middlename !== form.cw_middlename) {
			body.cw_middlename = form.cw_middlename
		}
		if (originalHHMember.emailaddress !== form.emailaddress) {
			body.emailaddress = form.emailaddress
		}
		if (isEmployee && !employeeId) {
			// new employee
			body.cw_relationship = 0
		} else if (originalHHMember.cw_relationship !== form.cw_relationship) {
			body.cw_relationship = Number(form.cw_relationship)
		}
		if (originalHHMember.cw_ssn !== form.cw_ssn) {
			if (!form.cw_ssn.includes("*")) {
				body.cw_ssn = form.cw_ssn
			}
		}
		if (originalHHMember.cw_hhzip !== form.cw_hhzip) {
			body.cw_hhzip = form.cw_hhzip
		}
		if (Number(originalHHMember.cw_gender) !== Number(form.cw_gender)) {
			body.cw_gender = form.cw_gender
		}
		if (originalHHMember.emailaddress !== form.emailaddress) {
			body.emailaddress = form.emailaddress
		}
		if (originalHHMember.cw_hhzip !== form.cw_hhzip) {
			body.cw_hhzip = form.cw_hhzip
		}
		if (!isEmployee && addHouseholdMember && employeeId) {
			body["cw_Employee@odata.bind"] = `/cw_employees(${employeeId})`
		}
		if (Object.keys(body).length > 0) {
			return body
		}
		return undefined
	}

	const getEmployeeSaveBody = (): Partial<IEmployeeCreate> => {
		const body: Partial<IEmployeeCreate> = {}
		if (originalEmployee) {
			if (originalEmployee.cw_jobtitle !== formEmployee.cw_jobtitle) {
				body.cw_jobtitle = formEmployee.cw_jobtitle
			}
			if (originalEmployee.cw_email !== formEmployee.cw_email) {
				body.cw_email = formEmployee.cw_email
			}
			if (originalEmployee.cw_city !== formEmployee.cw_city) {
				body.cw_city = formEmployee.cw_city
			}
			if (originalEmployee.cw_firstname !== form.cw_firstname || originalEmployee.cw_lastname !== form.cw_lastname) {
				body.cw_name = `${form.cw_firstname} ${form.cw_lastname}`
				if (originalEmployee.cw_firstname !== form.cw_firstname) {
					body.cw_firstname = form.cw_firstname
				}
				if (originalEmployee.cw_lastname !== form.cw_lastname) {
					body.cw_lastname = form.cw_lastname
				}
			}
			if (originalEmployee.cw_address !== formEmployee.cw_address) {
				body.cw_address = formEmployee.cw_address
			}
			if (originalEmployee.cw_addressline2 !== formEmployee.cw_addressline2) {
				body.cw_addressline2 = formEmployee.cw_addressline2
			}
			if (originalEmployee.cw_state !== formEmployee.cw_state) {
				body.cw_state = formEmployee.cw_state
			}
			if (originalEmployee.cw_zipcode !== form.cw_hhzip) {
				body.cw_zipcode = form.cw_hhzip
			}
			if (originalEmployee.cw_phone !== formEmployee.cw_phone) {
				body.cw_phone = formEmployee.cw_phone
			}
			if (safeParseDateTime(originalEmployee.cw_birthdate)?.toUTC().toFormat("yyyy-MM-dd") !== safeParseDateTime(form.cw_birthdate)?.toUTC().toFormat("yyyy-MM-dd")) {
				body.cw_birthdate = form.cw_birthdate
			}
			if (originalEmployee.cw_healthcarecoveragetype !== formEmployee.cw_healthcarecoveragetype) {
				body.cw_healthcarecoveragetype = formEmployee.cw_healthcarecoveragetype
			}
			if (originalEmployee.cw_ssn !== form.cw_ssn) {
				if (!form.cw_ssn.includes("*")) {
					body.cw_ssn = form.cw_ssn
				}
			}
			if (Number(originalEmployee.cw_gender) !== Number(form.cw_gender)) {
				body.cw_gender = form.cw_gender
			}
			if (originalEmployee._cw_plan_value !== formEmployee._cw_plan_value) {
				body["cw_Plan@odata.bind"] = `/cw_universalplanses(${formEmployee._cw_plan_value})`
			}
			if (originalEmployee._cw_department_value !== formEmployee._cw_department_value) {
				body["cw_Department@odata.bind"] = `/cw_departmentses(${formEmployee._cw_department_value})`
			}
			if (Object.keys(body).length > 0) {
				return body
			}
		}
		return undefined
	}

	/*
	Emp/Spouse	121640000
	Waived	121640001
	Family	121640002
	Spouse Only	121640003
	Emp/Child	121640004
	Child Only	121640005
	Emp Only	121640007
	Emp/Children	121640008
	*/
	const getHealthCareCoverageTypeForEmployee = (familyMembers: IHouseholdMemberEnrollmentManager[]): number => {
		let healthCareCoverageType = 121640007 //Emp Only (default)
		if (familyMembers.length > 1) {
			//more than one family member (including employee)
			if (!familyMembers.some(f => Number(f.cw_relationship) === 1)) {
				//no children
				healthCareCoverageType = 121640000 //Emp/Spouse
			} else if (!familyMembers.some(f => Number(f.cw_relationship) === 2) && familyMembers.length === 2) {
				//no spouse, one child
				healthCareCoverageType = 121640004 //Emp/Child
			} else if (!familyMembers.some(f => Number(f.cw_relationship) === 2) && familyMembers.length > 2) {
				//no spouse, multiple children
				healthCareCoverageType = 121640008 //Emp/Children
			} else {
				healthCareCoverageType = 121640002 //family
			}
		}
		return healthCareCoverageType
	}

	const editItem = async () => {
		const body = getFormSaveBody()

		let hhId
		if (Object.values(body).some(v => v !== undefined)) {
			const response = await makeCRMApiCall(
				addHouseholdMember ? "cw_householdmemberses" : `cw_householdmemberses(${householdMember.cw_householdmembersid})`,
				addHouseholdMember ? "POST" : "PATCH",
				JSON.stringify(body),
				undefined,
				["entityid"]
			)
			hhId = response?.entityid
		}

		if (!isEmployee && addHouseholdMember && employeeId) {
			const apiUrl = `getHouseholdMembers?accountId=${group.accountid}`
			const res = await makeACMGCRMApiCall(apiUrl)
			const results = res as IHouseholdMemberEnrollmentManager[]
			const employeeHousehold = results.filter(r => r.employee_id === employeeId)

			const filteredResults: IHouseholdMemberEnrollmentManager[] = []
			employeeHousehold.forEach(r => {
				const foundIndex = filteredResults.findIndex(f => f.cw_householdmembersid === r.cw_householdmembersid)
				if (foundIndex === -1) {
					filteredResults.push(r)
				}
			})

			const newCoverageType = getHealthCareCoverageTypeForEmployee(filteredResults)
			const employeeBody = { cw_healthcarecoveragetype: newCoverageType }
			await makeCRMApiCall(`cw_employees(${employeeId})`, "PATCH", JSON.stringify(employeeBody))
		}

		if (isEmployee && formEmployee) {
			const employeeBody = getEmployeeSaveBody()
			if (employeeBody && Object.values(employeeBody).some(v => v !== undefined)) {
				if (employeeId) {
					await makeCRMApiCall(`cw_employees(${employeeId})`, "PATCH", JSON.stringify(employeeBody))
				} else {
					employeeBody["cw_Employer@odata.bind"] = `/accounts(${group.accountid})`
					const employeeResponse = await makeCRMApiCall("cw_employees", "POST", JSON.stringify(employeeBody), undefined, ["entityid"])
					const employeeId = employeeResponse?.entityid
					await makeBrokerAPICall(group.accountid, `cw_householdmemberses(${hhId})`, "PATCH", {
						"cw_Employee@odata.bind": `/cw_employees(${employeeId})`
					})
				}
			}
		}
	}

	const _saveItem = async () => {
		try {
			setSaving(true)
			await editItem()
			setSaving(false)
			closePanel(true, true)
		} catch (err) {
			setSaving(false)
			setError(getErrorText(err))
		}
	}

	const panelTitle =
		isEmployee && addHouseholdMember ? "New Employee" : addHouseholdMember ? "New Household Member" : `Edit ${isEmployee ? "Employee" : "Household Member"}: ${householdMember.record.cw_name}`

	return (
		<Panel
			type={PanelType.custom}
			customWidth="700px"
			onDismiss={() => {
				closePanel(false, true)
			}}
			isOpen
			isFooterAtBottom
			headerText={panelTitle}
			onRenderFooterContent={() => {
				return (
					<Stack tokens={{ childrenGap: 10 }}>
						{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
						{saving && <ProgressIndicator label="This may take a minute...." />}
						<Stack horizontal tokens={{ childrenGap: 10 }}>
							<PrimaryButton onClick={_saveItem} disabled={saving || !madeChanges() || !isComplete()}>
								Save
							</PrimaryButton>
							<DefaultButton
								onClick={() => {
									closePanel(false, true)
								}}>
								Cancel
							</DefaultButton>
						</Stack>
					</Stack>
				)
			}}>
			<PanelForm
				key="editHouseholdMemberForm"
				disabled={saving}
				form={form}
				setForm={setForm}
				newHouseholdMember={addHouseholdMember}
				isEmployee={isEmployee}
				formEmployee={formEmployee}
				setFormEmployee={setFormEmployee}
				groupPlans={groupPlans}
				group={group}
			/>
		</Panel>
	)
}

interface PanelFormProps {
	form: Partial<IHouseholdMember>
	setForm: (form: React.SetStateAction<Partial<IHouseholdMember>>) => void
	formEmployee: Partial<IEmployee>
	setFormEmployee: (form: React.SetStateAction<Partial<IEmployee>>) => void
	newHouseholdMember: boolean
	isEmployee: boolean
	groupPlans: IGroupPlan[]
	disabled?: boolean
	group: IAccount
}

const handleChange = (field: string, value: any, setForm: (form: React.SetStateAction<Partial<IHouseholdMember | IEmployee>>) => void) => setForm(f => ({ ...f, [field]: value }))

function PanelForm(props: PanelFormProps) {
	const { form, setForm, disabled, newHouseholdMember, formEmployee, setFormEmployee, groupPlans, group } = props
	const isProspect = group.cw_accounttype.includes("Prospect")
	const groupPlanOptions: IDropdownOption[] = groupPlans.map(gp => ({ key: gp.cw_universalplansid, text: gp.cw_name }))
	const departments: IDropdownOption[] = group.departments?.map(p => ({ key: p.cw_departmentsid, text: p.cw_name }))
	const [birthdayDisplay, setBirthdayDisplay] = useState<string>(safeFormatDateValue(form?.cw_birthdate, "MM/dd/yyyy", ""))
	const [effDateDisplay, setEffDateDisplay] = useState<string>(safeFormatDateValue(form?.cw_insurancestartdate, "MM/dd/yyyy", ""))

	const relationshipOptions: IDropdownOption[] = [
		{ key: 0, text: "Employee" },
		{ key: 1, text: "Child" },
		{ key: 2, text: "Spouse" }
	]

	const genderOptions: IDropdownOption[] = [
		{ key: 121640000, text: "Male" },
		{ key: 121640001, text: "Female" },
		{ key: 121640002, text: "Transgender" },
		{ key: 121640003, text: "Non-binary/non-conforming" },
		{ key: 121640004, text: "Prefer not to respond" }
	]

	const cwCoverageTypes: IDropdownOption[] = [
		{ key: 121640007, text: "Emp Only" },
		{ key: 121640000, text: "Emp/Spouse" },
		{ key: 121640004, text: "Emp/Child" },
		{ key: 121640008, text: "Emp/Children" },
		{ key: 121640002, text: "Family" },
		{ key: 121640001, text: "Waived" }
		// { key: 121640003, text: "Spouse Only" },
		// { key: 121640005, text: "Child Only" },
	]

	const [birthdayError, setBirthdayError] = useState<string | undefined>()
	const [effDateError, setEffDateError] = useState<string | undefined>()
	const onDateChange = (fieldName: string, newValue: string | undefined, setDisplay: (val: string) => void, setError: (val: string | undefined) => void) => {
		const masked = maskAsDate(newValue || "")
		setDisplay(masked)
		setError(undefined)

		if (masked.length === 10) {
			const parsed = safeParseDateTime(masked)
			if (parsed?.isValid) {
				const dateValue = safeFormatDateValue(parsed, "yyyy-MM-dd")
				handleChange(fieldName, dateValue, setForm)
			} else {
				setError("Please enter a valid date.")
			}
		} else if (masked === "") {
			handleChange(fieldName, "", setForm)
		}
	}

	console.log("form before render", form)

	const editPlanDisabled = !IS_ADMIN && !props.newHouseholdMember && group.cw_lockunderwriting

	return (
		<Stack tokens={{ childrenGap: 12 }}>
			{props.isEmployee && formEmployee && (
				<Stack tokens={{ childrenGap: 12 }}>
					<h3 style={{ marginBottom: 0 }}>Employee Details</h3>
					<StackItem>
						<Dropdown
							disabled={editPlanDisabled}
							label="Plan"
							options={groupPlanOptions}
							selectedKey={formEmployee._cw_plan_value}
							onChange={(_ev, option) => {
								handleChange("_cw_plan_value", option.key, setFormEmployee)
							}}
						/>
					</StackItem>
					<StackItem>
						<Dropdown
							label="Coverage Type"
							disabled={editPlanDisabled}
							options={cwCoverageTypes}
							selectedKey={formEmployee.cw_healthcarecoveragetype}
							onChange={(_ev, option) => {
								handleChange("cw_healthcarecoveragetype", option.key, setFormEmployee)
							}}
						/>
					</StackItem>
					<StackItem>
						<Dropdown
							disabled={!IS_ADMIN || !departments || departments.length === 0}
							label="Department"
							options={departments}
							selectedKey={formEmployee._cw_department_value}
							onChange={(_ev, option) => {
								handleChange("_cw_department_value", option.key, setFormEmployee)
							}}
						/>
					</StackItem>
					<StackItem>
						<TextField
							label="Address"
							value={formEmployee?.cw_address}
							onChange={(_ev, newValue) => {
								handleChange("cw_address", newValue, setFormEmployee)
							}}
						/>
					</StackItem>
					<StackItem>
						<TextField
							label="Address Line 2"
							value={formEmployee?.cw_addressline2}
							onChange={(_ev, newValue) => {
								handleChange("cw_addressline2", newValue, setFormEmployee)
							}}
						/>
					</StackItem>
					<StackItem>
						<TextField
							label="City"
							value={formEmployee?.cw_city}
							onChange={(_ev, newValue) => {
								handleChange("cw_city", newValue, setFormEmployee)
							}}
						/>
					</StackItem>
					<StackItem>
						<TextField
							label="State"
							value={formEmployee?.cw_state}
							onChange={(_ev, newValue) => {
								handleChange("cw_state", newValue, setFormEmployee)
							}}
						/>
					</StackItem>
					<StackItem>
						<TextField
							label="Zip Code"
							required
							value={formEmployee?.cw_zipcode}
							onChange={(_ev, newValue) => {
								handleChange("cw_zipcode", newValue, setFormEmployee)
								handleChange("cw_hhzip", newValue, setForm)
							}}
						/>
					</StackItem>
				</Stack>
			)}
			<Stack tokens={{ childrenGap: 12 }}>
				<h3 style={{ marginBottom: 0 }}>Household Member Details</h3>
				<StackItem>
					<TextField
						disabled={disabled}
						label="First Name"
						required
						value={form?.cw_firstname}
						onChange={(ev, newValue) => {
							handleChange("cw_firstname", newValue, setForm)
						}}
					/>
				</StackItem>
				<StackItem>
					<TextField
						disabled={disabled}
						label="Middle Name"
						value={form?.cw_middlename}
						onChange={(ev, newValue) => {
							handleChange("cw_middlename", newValue, setForm)
						}}
					/>
				</StackItem>
				<StackItem>
					<TextField
						disabled={disabled}
						required
						label="Last Name"
						value={form?.cw_lastname}
						onChange={(ev, newValue) => {
							handleChange("cw_lastname", newValue, setForm)
						}}
					/>
				</StackItem>
				<StackItem>
					<Dropdown
						label="Gender"
						required
						selectedKey={form?.cw_gender ? Number(form.cw_gender) : undefined}
						onChange={(_ev, newValue) => {
							handleChange("cw_gender", newValue.key, setForm)
						}}
						options={genderOptions}
						disabled={disabled}
					/>
				</StackItem>
				<StackItem>
					<TextField
						disabled={disabled}
						label="Email"
						value={form?.emailaddress}
						onChange={(ev, newValue) => {
							handleChange("emailaddress", newValue, setForm)
							if (props.isEmployee) {
								handleChange("cw_email", newValue, setFormEmployee)
							}
						}}
					/>
				</StackItem>
				{IS_ADMIN && (
					<StackItem>
						<TextField
							label="Insurance Effective Date"
							placeholder="MM/DD/YYYY"
							inputMode="numeric"
							value={effDateDisplay}
							onChange={(_, newValue) => {
								onDateChange("cw_insurancestartdate", newValue, setEffDateDisplay, setEffDateError)
							}}
							maxLength={10}
							errorMessage={effDateError}
						/>
					</StackItem>
				)}
				<StackItem>
					<TextField
						disabled={!newHouseholdMember && !IS_ADMIN}
						label="SSN"
						value={form?.cw_ssn?.length > 5 ? form.cw_ssn : undefined}
						onChange={(_ev, newValue) => {
							handleChange("cw_ssn", newValue, setForm)
						}}
					/>
				</StackItem>
				{!(props.isEmployee && newHouseholdMember) && (
					<StackItem>
						<Dropdown
							label="Relationship"
							required
							selectedKey={form?.cw_relationship ? Number(form.cw_relationship) : undefined}
							onChange={(_ev, newValue) => {
								handleChange("cw_relationship", newValue.key, setForm)
							}}
							options={relationshipOptions}
							disabled={disabled}
						/>
					</StackItem>
				)}
				{!props.isEmployee && (
					<StackItem>
						<TextField
							label="Zip Code"
							required
							value={form?.cw_hhzip}
							onChange={(_ev, newValue) => {
								handleChange("cw_hhzip", newValue, setForm)
							}}
							disabled={disabled}
						/>
					</StackItem>
				)}

				<StackItem>
					<TextField
						label="Birth Date"
						required
						placeholder="MM/DD/YYYY"
						inputMode="numeric"
						value={birthdayDisplay}
						onChange={(_, newValue) => {
							onDateChange("cw_birthdate", newValue, setBirthdayDisplay, setBirthdayError)
						}}
						maxLength={10}
						errorMessage={birthdayError}
					/>
				</StackItem>
			</Stack>
		</Stack>
	)
}
